/**
 * Calendar Booking API Service
 * Public endpoints for candidate booking (no auth required)
 */

import http from '../client';
import { ENDPOINTS } from '../config';

/**
 * Get calendar data by URL slug
 * @param {string} url - Calendar URL identifier
 * @returns {Promise<Object>}
 */
/**
 * Authenticate candidate via calendar token (returns JWT)
 * @param {string} calendarToken - Calendar invitation token
 * @returns {Promise<Object>}
 */
export const authenticateByCalendarToken = async (calendarToken) => {
  try {
    const response = await http.post(ENDPOINTS.CALENDAR.AUTH(calendarToken), null, { skipAuth: true });
    return { data: response?.data || null, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

export const getCalendarByUrl = async (url) => {
  try {
    const response = await http.get(ENDPOINTS.CALENDAR.BY_URL(url), null, { skipAuth: true });
    return response;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Get candidate data by invitation token
 * @param {string} token - Invitation token
 * @returns {Promise<Object>}
 */
export const getCandidateByToken = async (token) => {
  try {
    const response = await http.get(ENDPOINTS.CALENDAR.CANDIDATE_BY_TOKEN(token), null, { skipAuth: true });
    return response;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Get existing booking by token
 * @param {string} token - Calendar token
 * @returns {Promise<Object>}
 */
export const getBookingByToken = async (token) => {
  try {
    const response = await http.get(ENDPOINTS.CALENDAR.MEETING_BY_TOKEN(token), null, { skipAuth: true });
    return response;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Create a new booking
 * @param {Object} data - Booking data (calendarId, date, startTime, endTime, talent, calendarToken)
 * @returns {Promise<Object>}
 */
export const createBooking = async (data) => {
  try {
    const response = await http.post(ENDPOINTS.CALENDAR.MEETING_BOOKING, data, { skipAuth: true });
    return response;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Update an existing booking (reschedule)
 * @param {number} id - Booking ID
 * @param {Object} data - Updated booking data
 * @returns {Promise<Object>}
 */
export const updateBooking = async (id, data) => {
  try {
    const response = await http.put(ENDPOINTS.CALENDAR.MEETING_UPDATE(id), data, { skipAuth: true });
    return response;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export default {
  getCalendarByUrl,
  getCandidateByToken,
  getBookingByToken,
  createBooking,
  updateBooking
};
